import type { CompanyId, HolidayId } from '@shared/brand';
import type { CalendarDate } from '../value-objects/CalendarDate';
import type { HolidayType } from '../vocabulary';
import type { Auditable } from './Organization';

/**
 * A public holiday on the company calendar.
 *
 * `enabled` rather than deletion: HR turns a customary day off for one year
 * without losing the record, and a disabled holiday must stop affecting the
 * business-day count immediately — for everyone, without a redeploy. That is why
 * holidays are data, not a bundled table.
 *
 * `year` is stored alongside `date` even though it is derivable: every query is
 * "the calendar for 2026", and Firestore cannot filter on a computed prefix.
 */
export interface Holiday extends Auditable {
  readonly id: HolidayId;
  readonly companyId: CompanyId;
  readonly year: number;
  readonly date: CalendarDate;
  readonly name: string;
  /**
   * `Obligatorio` is mandated by the Ley Federal del Trabajo; `Por costumbre` is
   * granted by the company. Both are discounted from the business-day count when
   * enabled — the distinction is informational, shown so HR knows which days it
   * is free to switch off.
   */
  readonly type: HolidayType;
  readonly enabled: boolean;
}

/** The `YYYY-MM-DD` strings of the holidays that currently count. */
export const enabledHolidayDates = (holidays: readonly Holiday[]): ReadonlySet<string> =>
  new Set(holidays.filter((h) => h.enabled).map((h) => h.date.toISO()));

/**
 * The nth given weekday of a month, e.g. the third Monday of November. Mexico's
 * LFT moves several holidays to a Monday, so those dates shift from year to year
 * and cannot be a fixed list.
 */
const nthWeekdayOfMonth = (
  year: number,
  /** 1-12. */
  month: number,
  /** 0 = Sunday. */
  weekday: number,
  occurrence: number,
): { year: number; month: number; day: number } => {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const shiftToWeekday = (weekday - firstOfMonth.getUTCDay() + 7) % 7;
  const day = 1 + shiftToWeekday + (occurrence - 1) * 7;
  return { year, month, day };
};

export interface HolidaySeed {
  readonly date: string;
  readonly name: string;
  readonly type: Exclude<HolidayType, 'Desconocido'>;
}

const iso = (parts: { year: number; month: number; day: number }): string =>
  `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;

const MONDAY = 1;

/**
 * The statutory Mexican holidays for a given year, computed from the LFT rules
 * rather than hard-coded, so seeding 2027 needs no code change. Customary days
 * (Semana Santa, Día de Muertos, Guadalupe) are not included: they depend on the
 * liturgical calendar or on company practice, and HR adds them from the
 * Feriados screen.
 */
export const statutoryHolidaysFor = (year: number): readonly HolidaySeed[] => [
  { date: `${year}-01-01`, name: 'Año Nuevo', type: 'Obligatorio' },
  {
    date: iso(nthWeekdayOfMonth(year, 2, MONDAY, 1)),
    name: 'Día de la Constitución',
    type: 'Obligatorio',
  },
  {
    date: iso(nthWeekdayOfMonth(year, 3, MONDAY, 3)),
    name: 'Natalicio de Benito Juárez',
    type: 'Obligatorio',
  },
  { date: `${year}-05-01`, name: 'Día del Trabajo', type: 'Obligatorio' },
  { date: `${year}-09-16`, name: 'Independencia de México', type: 'Obligatorio' },
  {
    date: iso(nthWeekdayOfMonth(year, 11, MONDAY, 3)),
    name: 'Revolución Mexicana',
    type: 'Obligatorio',
  },
  { date: `${year}-12-25`, name: 'Navidad', type: 'Obligatorio' },
];

/**
 * Customary days the handoff's 2026 calendar includes. Offered as a starting
 * point when HR seeds a year; each one is theirs to keep or drop.
 *
 * Easter-derived dates are computed with the anonymous Gregorian algorithm so
 * Jueves and Viernes Santo land correctly in any year.
 */
export const customaryHolidaysFor = (year: number): readonly HolidaySeed[] => {
  const easter = easterSunday(year);
  const goodFriday = shiftDays(easter, -2);
  const holyThursday = shiftDays(easter, -3);
  return [
    { date: iso(holyThursday), name: 'Jueves Santo', type: 'Por costumbre' },
    { date: iso(goodFriday), name: 'Viernes Santo', type: 'Por costumbre' },
    { date: `${year}-11-02`, name: 'Día de Muertos', type: 'Por costumbre' },
    { date: `${year}-12-12`, name: 'Día de la Virgen de Guadalupe', type: 'Por costumbre' },
  ];
};

/** Anonymous Gregorian computus. */
const easterSunday = (year: number): { year: number; month: number; day: number } => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { year, month, day };
};

const shiftDays = (
  parts: { year: number; month: number; day: number },
  offset: number,
): { year: number; month: number; day: number } => {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + offset));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
};
